import React, { useContext, useState } from 'react';
import { useGlobalEvent } from 'beautiful-react-hooks';
import DeleteConfirmationModal from '~/App/Components/Modals/DeleteConfirmationModal';
import { useCancelAddOn } from '~/App/Modules/NewBilling/Hooks/useAddOns';
import { useQueryClient } from 'react-query';

import FlashContext from '~/App/Components/Flash/Context';
import useCurrentTeam from '~/react-hooks/use-current-team';

import { queryKeys as invoiceInfoKeys } from '../../../../Hooks/useInvoiceInfo';
import { queryKeys as subscriptionKeys } from '../../../../Hooks/useSubscription';
import { queryKeys as addOnKeys } from '../../../../Hooks/useAddOns';

const openRollUpLimitCancelModalEventName = 'roll-up-limit-cancel-modal::open';
const closeRollUpLimitCancelModalEventName =
  'roll-up-limit-cancel-modal::close';

export interface OpenRollUpLimitAddOnCancelModalEventDetails {
  addOnUuid: string;
}

export const openRollUpLimitAddOnCancelModal = ({
  addOnUuid,
}: OpenRollUpLimitAddOnCancelModalEventDetails) => {
  window.dispatchEvent(
    new CustomEvent(openRollUpLimitCancelModalEventName, {
      detail: { addOnUuid },
    })
  );
};

export const closeRollUpLimitAddOnCancelModal = () =>
  window.dispatchEvent(new CustomEvent(closeRollUpLimitCancelModalEventName));

export const RollUpLimitAddOnCancelModal = () => {
  const [open, setOpen] = useState(false);
  const [addOnUuid, setAddOnUuid] = useState<string>('');
  const team = useCurrentTeam();

  const queryClient = useQueryClient();
  const { createFlash } = useContext(FlashContext);

  const onOpenRollUpLimitAddOCancelModal = useGlobalEvent<
    CustomEvent<OpenRollUpLimitAddOnCancelModalEventDetails>
  >(openRollUpLimitCancelModalEventName as keyof WindowEventMap);
  const onCloseRollUpLimitAddOnCancelModal = useGlobalEvent(
    closeRollUpLimitCancelModalEventName as keyof WindowEventMap
  );

  onOpenRollUpLimitAddOCancelModal((event) => {
    setOpen(true);
    setAddOnUuid(event.detail.addOnUuid);
  });
  onCloseRollUpLimitAddOnCancelModal(() => setOpen(false));

  const closeModal = () => setOpen(false);

  const { mutateAsync: cancelRollUpLimitAddOn, isLoading: cancelling } =
    useCancelAddOn({
      teamId: team.id,
      addOnUuid,
      options: {
        onSuccess: () => {
          setOpen(false);
          queryClient.invalidateQueries('app-config');
          queryClient.invalidateQueries(invoiceInfoKeys.detail);
          queryClient.invalidateQueries(addOnKeys.detail);
          queryClient.invalidateQueries(subscriptionKeys.lists());
          createFlash({
            id: 'cancel-roll-up-limit-add-on-success',
            title: 'Success',
            message: 'Growth lite add-on is now cancelled. Redirecting...',
          });
          window.location.reload();
        },
        onError: () => {
          createFlash({
            id: 'cancel-roll-up-limit-add-on-failed',
            title: 'Error',
            message:
              'Something went wrong while processing your request. Please try again.',
            options: { style: 'danger' },
          });
        },
      },
    });

  const onDeleteClick = async () => {
    await cancelRollUpLimitAddOn();
  };

  return (
    <DeleteConfirmationModal
      open={open}
      title='Are you sure you want to cancel Growth lite add-on? You will lose access to Roll-ups.'
      onConfirm={onDeleteClick}
      confirmCtaText='Delete'
      onCancel={closeModal}
      loading={cancelling}
    />
  );
};
